import * as dagre from 'dagre';
import * as R from 'ramda';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import type { IntlShape } from '@atlassian/jira-intl';
import { getLocale } from '@atlassian/jira-platform-utils-date-fns';
import {
	ONE_DAY,
	distanceInWordsByShortFormat,
	convertShortFormatDate,
	EPIC_LEVEL,
} from '@atlassian/jira-portfolio-3-common';
import type {
	GraphLinks,
	TargetIssueLink,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/dependencies/types';
import type { IssueLinkType } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/issue-links';
import {
	getIssueLinkIfOverlapping,
	getIssueLinkLeadTime,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/issue-links/util';
import type { IssueStatusesById } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/issue-statuses/types';
import type { Issue } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/issues';
import type { Sprint } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/sprints';
import type { Team } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/teams/types';
import { ReportStatus } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/ui/main/tabs/dependencies/report-state/types';
import type { StagePosAndScale } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/ui/main/tabs/dependencies/stage-position-scale/types';
import type { IssueLink } from '@atlassian/jira-portfolio-3-portfolio/src/common/api/types';
import { isDefined } from '@atlassian/jira-portfolio-3-portfolio/src/common/ramda';
import {
	VALID_LEADING_TIME_FOR_DEPENDENCIES,
	PACKAGE_NAME,
	ERROR_REPORTING_PACKAGE,
	ERROR_REPORTING_TEAM,
} from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';
import messages from './messages';
import {
	UNASSIGNED,
	type GroupByNotNoneType,
	NO_PARENT_GROUP_SUFFIX,
	OTHER_GROUP_ID,
	GroupBy,
	type IssueWithGroupBySprintProp,
	GROUP_BY_SPRINT_FIELD,
	type Measurements,
	type Rect,
	type LayoutErrorResponse,
} from './types';

const DEFAULT_GRAPH_PROPS = {
	rankdir: 'LR',
	nodesep: 50,
	edgesep: 30,
	ranksep: 150,
	marginx: 20,
	marginy: 20,
	align: 'UL',
};
export const getGroupIdFromGroupKey = (key: string, groupBy: string): string =>
	key.substring(groupBy.length);

const getSubGroupKeyForNoParentGroup = (groupKey: string): string =>
	`${groupKey}:${NO_PARENT_GROUP_SUFFIX}`;

const drawIssueWithoutParentGroupBox = (
	g: dagre.graphlib.Graph<{}>,
	groupKey: string,
	issueIds: string[],
	parentGroupKey?: string,
): dagre.graphlib.Graph<{}> => {
	if (issueIds.length > 0) {
		const nodeHeight = 70 + issueIds.length * 44 - 8;
		g.setNode(groupKey, {
			width: 300,
			// 355px allows for 6 issues displayed + the 7th cropped in half
			height: Math.min(nodeHeight, 355),
		});
		if (isDefined(parentGroupKey)) {
			g.setParent(groupKey, parentGroupKey);
		}
	}
	return g;
};

const updateDependencyLink = (
	otherIssueSubGroupMap: { [issueKey: string]: string },
	initialDependencyLinks: GraphLinks,
	sourceKey: string,
	targets: TargetIssueLink,
	otherIssuesInSubGroup: string[],
): GraphLinks => {
	Object.keys(targets).forEach((targetKey) => {
		if (targetKey === OTHER_GROUP_ID || sourceKey === OTHER_GROUP_ID) {
			const links = targets[targetKey].links;
			const linkInSubGroup: IssueLink[] = [];
			links.forEach((link) => {
				const { sourceItemKey, targetItemKey } = link;
				let subGroupKeyForSouceItem = sourceKey;
				let subGroupKeyForTargetItem = targetKey;
				if (sourceKey === OTHER_GROUP_ID && targetKey === OTHER_GROUP_ID) {
					subGroupKeyForTargetItem = otherIssuesInSubGroup.includes(targetItemKey)
						? getSubGroupKeyForNoParentGroup(otherIssueSubGroupMap[targetItemKey])
						: OTHER_GROUP_ID;
					subGroupKeyForSouceItem = otherIssuesInSubGroup.includes(sourceItemKey)
						? getSubGroupKeyForNoParentGroup(otherIssueSubGroupMap[sourceItemKey])
						: OTHER_GROUP_ID;
					if (otherIssuesInSubGroup.includes(sourceItemKey)) {
						linkInSubGroup.push(link);
					}
				} else if (
					sourceKey === OTHER_GROUP_ID &&
					targetKey !== OTHER_GROUP_ID &&
					otherIssuesInSubGroup.includes(sourceItemKey)
				) {
					subGroupKeyForSouceItem = getSubGroupKeyForNoParentGroup(
						otherIssueSubGroupMap[sourceItemKey],
					);
					subGroupKeyForTargetItem = targetKey;
					linkInSubGroup.push(link);
				} else if (
					sourceKey !== OTHER_GROUP_ID &&
					targetKey === OTHER_GROUP_ID &&
					otherIssuesInSubGroup.includes(targetItemKey)
				) {
					subGroupKeyForTargetItem = otherIssuesInSubGroup.includes(targetItemKey)
						? getSubGroupKeyForNoParentGroup(otherIssueSubGroupMap[targetItemKey])
						: OTHER_GROUP_ID;
					subGroupKeyForSouceItem = sourceKey;
					linkInSubGroup.push(link);
				}
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				const originalLinks = (R.path(
					[subGroupKeyForSouceItem, subGroupKeyForTargetItem, 'links'],
					initialDependencyLinks,
				) || []) as IssueLink[];
				if (!isDefined(initialDependencyLinks[subGroupKeyForSouceItem])) {
					// eslint-disable-next-line no-param-reassign
					initialDependencyLinks[subGroupKeyForSouceItem] = {};
				}
				if (!isDefined(initialDependencyLinks[subGroupKeyForSouceItem][subGroupKeyForTargetItem])) {
					// eslint-disable-next-line no-param-reassign
					initialDependencyLinks[subGroupKeyForSouceItem][subGroupKeyForTargetItem] = {
						links: [],
						highlight: false,
						rollup: false,
						type: 'issue',
					};
				}

				// eslint-disable-next-line no-param-reassign
				initialDependencyLinks[subGroupKeyForSouceItem][subGroupKeyForTargetItem].links =
					originalLinks.concat(link);
				// eslint-disable-next-line no-param-reassign
				initialDependencyLinks[subGroupKeyForSouceItem][subGroupKeyForTargetItem] = R.mergeLeft(
					R.pick(['highlight', 'rollup', 'type'], targets[targetKey]),
					initialDependencyLinks[subGroupKeyForSouceItem][subGroupKeyForTargetItem],
				);
			});
			if (linkInSubGroup.length === links.length) {
				// eslint-disable-next-line no-param-reassign
				delete initialDependencyLinks[sourceKey][targetKey];
			} else {
				// eslint-disable-next-line no-param-reassign
				initialDependencyLinks[sourceKey][targetKey].links = R.without(linkInSubGroup, links);
			}
		}
	});

	return initialDependencyLinks;
};
/**
 * Group the issues without parent(called other issue in this context) into sub groups,
 * @param otherIssueSubGroupMap
 * @param dependencyLinks
 * @returns
 */
const updateDependencyLinks = (
	otherIssueSubGroupMap: { [issueKey: string]: string },
	dependencyLinks: GraphLinks,
): GraphLinks => {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const otherIssuesInSubGroup = R.keys(otherIssueSubGroupMap) as string[];
	let initialDependencyLinks = R.clone(dependencyLinks);
	Object.keys(dependencyLinks).forEach((sourceKey) => {
		const targets = dependencyLinks[sourceKey];
		initialDependencyLinks = updateDependencyLink(
			otherIssueSubGroupMap,
			initialDependencyLinks,
			sourceKey,
			targets,
			otherIssuesInSubGroup,
		);
		if (Object.keys(initialDependencyLinks[sourceKey]).length === 0) {
			delete initialDependencyLinks[sourceKey];
		}
	});
	return initialDependencyLinks;
};

/**
 * Add a groupBySprintField property to the issue, the value of this property can be the sprint of the issue or the latest completed sprint
 * @param issues
 * @param sprintsById
 * @returns
 */
const addGroupBySprintFieldToIssue = (
	issues: Issue[],
	sprintsById: {
		[key: string]: Sprint;
	},
): IssueWithGroupBySprintProp[] =>
	issues.map((issue) => {
		const { sprint, completedSprints } = issue;
		if (isDefined(sprint)) {
			return R.assocPath([GROUP_BY_SPRINT_FIELD], sprint, issue);
		}
		if (isDefined(completedSprints) && completedSprints.length > 0) {
			const latestCompletedSprintId = completedSprints.reduce(
				(latestSprintId: string | null, currentSprintId: string) => {
					const currentSprint = isDefined(currentSprintId) && sprintsById[`${currentSprintId}`];
					const latestSprint = isDefined(latestSprintId) && sprintsById[`${latestSprintId}`];
					const latestSprintEndDate =
						typeof latestSprint === 'object' && (latestSprint.completeDate || latestSprint.endDate);
					const currentSprintEndDate =
						typeof currentSprint === 'object' &&
						(currentSprint.completeDate || currentSprint.endDate);
					if (typeof latestSprintEndDate === 'number' && typeof currentSprintEndDate === 'number') {
						return latestSprintEndDate < currentSprintEndDate ? currentSprintId : latestSprintId;
					}
					return typeof currentSprintEndDate === 'number' ? currentSprintId : null;
				},
				null,
			);
			if (isDefined(latestCompletedSprintId)) {
				return R.assocPath([GROUP_BY_SPRINT_FIELD], latestCompletedSprintId, issue);
			}
		}
		return issue;
	});

// Re-running the dagre.layout function when failed with different rank algorithm.
// The error seems to be triggered when the layer matrix has some undefined values.
// The undefined values come from the non-sequential values from the node ranks, that are set by the ranker algorithm.
// https://hello.atlassian.net/wiki/spaces/AG/pages/2688156205/Investigating+dependency+report+layout+error#Latest-findings
const runLayout = (g: dagre.graphlib.Graph): LayoutErrorResponse | null => {
	// Run the default algorithm first - network-simplex, then tight-tree.
	// Last is longest-path as the doc says it's "a fast and simple ranker, but results are far from optimal".
	// Source: https://github.com/dagrejs/dagre/blob/master/lib/rank/index.js#L29
	const rankers = ['network-simplex', 'tight-tree', 'longest-path'];
	let response: LayoutErrorResponse | null = null;

	for (const ranker of rankers) {
		try {
			g.setGraph({
				ranker,
				...DEFAULT_GRAPH_PROPS,
			});
			dagre.layout(g);
			// break the loop if the layout function is successful
			return null;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (e: any) {
			// record the error if layout function fails and continue the loop
			response = {
				ranker,
				error: e,
			};
		}
	}
	// return the last response
	return response;
};

export function layoutIssues(
	intl: IntlShape,
	isLoading: boolean,
	issuesById: {
		[id: string]: Issue;
	},
	dependencyLinks: GraphLinks,
	issueLinkTypesById: Record<number | string, IssueLinkType>,
	groupBy: GroupBy | null,
	teamsById: {
		[key: string]: Team;
	},
	rollupBy: string | null,
	otherIssues: string[],
	syncStartEnabled: boolean,
	issueStatusesById: IssueStatusesById,
	sprintsById: {
		[key: string]: Sprint;
	},
	locale: string,
) {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const labels: Record<string, any> = {};
	const g = new dagre.graphlib.Graph({ compound: true });

	const ISSUE_SIZE = { width: 300, height: 108 };
	const HEADER_SIZE = { width: 250, height: 60 };

	// Store the other issues which had been include in the sub group without parent
	const otherIssuesInSubGroup: string[] = [];
	const otherIssuesInSubGroupMap: { [groupKey: string]: string[] } = {};
	const isGroupBySprint = groupBy === GroupBy.SPRINT;
	let groupsOfIssues: string[] = [];

	// Return early if it's still loading
	if (isLoading) {
		g.setGraph(DEFAULT_GRAPH_PROPS);
		return {
			status: ReportStatus.LOADING,
			graph: g,
			labels,
			groupsOfIssues: new Set<string>(groupsOfIssues),
			itemIdsForGraph: [],
			otherIssuesInGroupMap: otherIssuesInSubGroupMap,
		};
	}

	const getGroupKey = (groupId: string | number | null | undefined): string | undefined =>
		isDefined(groupId) ? `${groupBy}${groupId}` : undefined;

	const getGroupKeyForIssue = (issue: IssueWithGroupBySprintProp): string | undefined | null => {
		if (isGroupBySprint) {
			return getGroupKey(issue[GROUP_BY_SPRINT_FIELD]);
		}
		// Need to add the if condition to fix TS error
		// Group by none and epic_level won't be using this function
		if (isDefined(groupBy) && groupBy !== 'none' && groupBy !== 'epic_level') {
			return getGroupKey(issue[groupBy]);
		}

		return null;
	};

	// Default to assigning a new object as a label for each new edge.
	g.setDefaultEdgeLabel(() => ({}));

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	let itemIdsForGraph = Object.keys(dependencyLinks).reduce<Array<any>>(
		(acc, sourceKey: string) => {
			const targets: TargetIssueLink = dependencyLinks[sourceKey];
			const ids = Object.entries(targets).reduce((pre: string[], [targetKey]) => {
				pre.push(sourceKey);
				pre.push(targetKey);
				return pre;
			}, []);

			acc.push(...ids);
			return acc;
		},
		[],
	);
	itemIdsForGraph = [...new Set(itemIdsForGraph)];
	if (rollupBy === 'team') {
		const teamsForGraph = itemIdsForGraph.filter((id) => teamsById[id]).map((id) => teamsById[id]);
		teamsForGraph.forEach((team) => {
			g.setNode(team.id, { label: team.title, ...HEADER_SIZE });
		});
	} else {
		const issues = (() => {
			// eslint-disable-next-line @typescript-eslint/no-shadow
			const issues = itemIdsForGraph.filter((id) => issuesById[id]).map((id) => issuesById[id]);
			if (isGroupBySprint) {
				return addGroupBySprintFieldToIssue(issues, sprintsById);
			}
			return issues;
		})();

		const otherIssuesUpdated = (() => {
			// eslint-disable-next-line @typescript-eslint/no-shadow
			const issues = otherIssues.filter((id) => issuesById[id]).map((id) => issuesById[id]);
			if (isGroupBySprint) {
				return addGroupBySprintFieldToIssue(issues, sprintsById);
			}
			return issues;
		})();

		if (groupBy === GroupBy.EPIC_LEVEL) {
			const epicGroups: string[] = [];
			const epics: Issue[] = [];

			issues.forEach((issue) => {
				const { id, summary, level, parent } = issue;
				if (level === EPIC_LEVEL) {
					epics.push(issue);
				} else {
					g.setNode(id, { label: summary, ...ISSUE_SIZE });
					const issueParent = parent ? issuesById[parent] : null;

					const groupKey =
						issueParent && issueParent.level === EPIC_LEVEL ? getGroupKey(issueParent.id) : null;

					if (isDefined(groupKey)) {
						g.setParent(id, groupKey);
						epicGroups.push(groupKey);
					} else {
						// We still need to assign a group to issues with no epic to prevent error by the dagre plugin
						g.setNode(UNASSIGNED, { label: UNASSIGNED, clusterLabelPos: 'top' });
						g.setParent(issue.id, UNASSIGNED);
					}
				}
			});
			epics.forEach(({ id, summary }) => {
				g.setNode(id, { label: summary, ...ISSUE_SIZE });
				const groupKey = getGroupKey(id);
				g.setParent(id, groupKey && epicGroups.includes(groupKey) ? groupKey : UNASSIGNED);
			});
			groupsOfIssues = [...new Set(epicGroups)];

			groupsOfIssues.forEach((groupId) => {
				g.setNode(groupId, { label: groupId, clusterLabelPos: 'top' });
			});
		} else {
			const isGrouped = groupBy !== GroupBy.NONE && groupBy !== null;
			const notNoneChecks = groupBy !== 'none'; // Need this to fix TS error
			if (isGrouped) {
				const allIssues: IssueWithGroupBySprintProp[] = issues.concat(otherIssuesUpdated);
				const groupByField = isGroupBySprint ? GROUP_BY_SPRINT_FIELD : groupBy;
				groupsOfIssues = [
					...new Set(
						allIssues
							.filter(
								(issue) =>
									// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
									notNoneChecks && issue[groupByField as GroupByNotNoneType],
							)
							.map((issue) => {
								const groupKey = getGroupKeyForIssue(issue);
								return isDefined(groupKey) ? groupKey : '';
							}),
					),
				];

				groupsOfIssues.forEach((groupId) => {
					g.setNode(groupId, { label: groupId, clusterLabelPos: 'top' });
				});
			}
			issues.forEach((issue) => {
				g.setNode(issue.id, { label: issue.summary, ...ISSUE_SIZE });

				if (isGrouped) {
					const groupKey = (() => getGroupKeyForIssue(issue))();
					if (isDefined(groupKey)) {
						g.setParent(issue.id, groupKey);
					} else {
						// We still need to assign a group to issues with no epic to prevent error by the dagre plugin
						g.setNode(UNASSIGNED, { label: UNASSIGNED, clusterLabelPos: 'top' });
						g.setParent(issue.id, UNASSIGNED);
					}
				}
			});
			if (otherIssuesUpdated.length && isGrouped) {
				otherIssuesUpdated.forEach((issue) => {
					const groupKey = getGroupKeyForIssue(issue);
					if (groupKey) {
						if (otherIssuesInSubGroupMap[groupKey]) {
							otherIssuesInSubGroupMap[groupKey].push(issue.id);
						} else {
							otherIssuesInSubGroupMap[groupKey] = [issue.id];
						}
						otherIssuesInSubGroup.push(issue.id);
					}
				});
				// draw the sub group (without parent) in the group
				R.keys(otherIssuesInSubGroupMap).forEach((groupKey) => {
					drawIssueWithoutParentGroupBox(
						g,
						getSubGroupKeyForNoParentGroup(`${groupKey}`),
						otherIssuesInSubGroupMap[groupKey],
						`${groupKey}`,
					);
				});
			}
		}
	}

	if (otherIssues.length) {
		const otherIssuesNotInSubGroup = R.without(otherIssuesInSubGroup, otherIssues);
		drawIssueWithoutParentGroupBox(g, 'other', otherIssuesNotInSubGroup);
	}

	let dependencyLinksUpdated = dependencyLinks;
	const isGrouped = groupBy !== GroupBy.NONE && groupBy !== null;
	if (isGrouped) {
		const otherIssueSubGroupMap = Object.keys(otherIssuesInSubGroupMap).reduce(
			(acc: { [issueKey: string]: string }, groupKey) => {
				const issueIds = otherIssuesInSubGroupMap[groupKey];
				issueIds.forEach((issueId: string) => {
					acc[issueId] = groupKey;
					return acc;
				});
				return acc;
			},
			{},
		);
		// Also need to update the issue links
		dependencyLinksUpdated = updateDependencyLinks(otherIssueSubGroupMap, dependencyLinks);
	}

	Object.keys(dependencyLinksUpdated).forEach((sourceKey) => {
		const targets = dependencyLinksUpdated[sourceKey];

		Object.keys(targets).forEach((targetKey) => {
			const { links, rollup, highlight } = targets[targetKey];

			const leadTime = getIssueLinkLeadTime(issuesById[sourceKey], issuesById[targetKey]);
			const isOverlapping = getIssueLinkIfOverlapping(
				issuesById[sourceKey],
				issuesById[targetKey],
				issueStatusesById,
				syncStartEnabled,
			);
			let leadTimeMessage = '';
			if (isDefined(leadTime)) {
				// if "end date" of issue A is May 4, 2021 and "start date" of issue B is May 5, 2021, then leadTime = 1
				if (leadTime === 1) {
					// we display a leadTimeMessage "less than a day"
					// note: we don't use distanceInWordsToNowWithPolarity as it would return 'less than a minute'
					leadTimeMessage = intl.formatMessage(messages.lessThanOneDayLeadTimeShortFormat);
				} else if (
					// if "end date" of issue A is May 4, 2021 and "start date" of issue B is May 4, 2021, then leadTime = -86399999
					Math.abs(leadTime) < ONE_DAY ||
					(leadTime < 0 && !isOverlapping && syncStartEnabled)
				) {
					// we display a leadTimeMessage of "0 days"
					leadTimeMessage = convertShortFormatDate(0, 'day', getLocale(locale)?.code);
				} else {
					leadTimeMessage = distanceInWordsByShortFormat(Date.now(), Date.now() + leadTime, locale);
				}
			}

			let label = '';
			if (rollup) {
				label = intl.formatMessage(messages.rollupLinkCountLabel, {
					count: links.length,
				});
			} else if (links.length > 1) {
				label = intl.formatMessage(messages.multipleLinks);
			} else {
				const linkType = issueLinkTypesById[links[0].type];
				if (isDefined(leadTime)) {
					label = `${linkType.outward} (${leadTimeMessage})`;
				} else {
					label = linkType.outward;
				}
			}

			labels[`${sourceKey}:${targetKey}`] = {
				label,
				isRollup: rollup,
				links,
				highlight,
				isNegativeLeadTime:
					// note: leadTime is the difference between targetIssue.baselineStart and sourceIssue.baselineEnd
					isDefined(leadTime) &&
					leadTime < 0 &&
					Math.abs(leadTime) > VALID_LEADING_TIME_FOR_DEPENDENCIES &&
					!(!isOverlapping && syncStartEnabled),
			};

			if (sourceKey !== targetKey) {
				g.setEdge(sourceKey, targetKey);
			}
		});
	});

	function getErrorAttributes(response: LayoutErrorResponse) {
		return {
			message: response.error.message ?? 'Dagre layout function failure',
			ranker: response.ranker,
			groupBy,
			rollupBy,
			groupsCount: Array.isArray(groupsOfIssues) ? groupsOfIssues.length : null,
			itemIdsCount: Array.isArray(itemIdsForGraph) ? itemIdsForGraph.length : null,
			otherIssuesCount: Array.isArray(otherIssues) ? otherIssues.length : null,
			...(g && {
				edgeCount: g.edgeCount && g.edgeCount(),
				nodeCount: g.nodeCount && g.nodeCount(),
			}),
		};
	}

	const errorResponse: LayoutErrorResponse | null = runLayout(g);
	if (errorResponse) {
		fireErrorAnalytics({
			meta: {
				packageName: PACKAGE_NAME,
				id: ERROR_REPORTING_PACKAGE.DEPENDENCIES,
				teamName: ERROR_REPORTING_TEAM,
			},
			attributes: getErrorAttributes(errorResponse),
			error: errorResponse.error,
			sendToPrivacyUnsafeSplunk: true,
		});

		return {
			status: ReportStatus.WARNING,
			graph: g,
			labels,
			groupsOfIssues: new Set<string>(groupsOfIssues),
			itemIdsForGraph: [],
			otherIssuesInGroupMap: otherIssuesInSubGroupMap,
		};
	}

	return {
		status: ReportStatus.SUCCESS,
		graph: g,
		labels,
		groupsOfIssues: new Set<string>(groupsOfIssues),
		itemIdsForGraph,
		otherIssuesInGroupMap: otherIssuesInSubGroupMap,
	};
}

export const calculateFitPosition = (
	wRect: Measurements,
	layoutWidth: number,
	layoutHeight: number,
): StagePosAndScale => {
	const wRatio = layoutWidth / wRect.width;
	const hRatio = layoutHeight / wRect.height;
	const ratio = Math.max(wRatio, hRatio);
	const scale = Math.min(1, 1 / ratio);
	const x = wRect.width / 2 - (layoutWidth * scale) / 2;
	const y = wRect.height / 2 - (layoutHeight * scale) / 2;
	return {
		x,
		y,
		scale,
	};
};

export const nextZoomStep = (zoomSteps: number[], currentZoom: number, zoomingIn: boolean) => {
	const getZoomStep = (index: number) => zoomSteps[R.clamp(0, zoomSteps.length - 1, index)];
	// No step is smaller
	if (currentZoom < getZoomStep(0)) {
		return zoomingIn ? getZoomStep(0) : currentZoom;
	}
	for (let i = 1; i < zoomSteps.length; i++) {
		if (zoomSteps[i] === currentZoom) {
			return zoomingIn ? getZoomStep(i + 1) : getZoomStep(i - 1);
		}
		if (zoomSteps[i] > currentZoom) {
			return zoomingIn ? getZoomStep(i) : getZoomStep(i - 1);
		}
	}
	// No step is bigger
	return zoomingIn ? currentZoom : getZoomStep(zoomSteps.length - 1);
};

export const calculatePositionalZoom = (
	nextScale: number,
	stagePos: StagePosAndScale,
	wrapperRect: Rect,
	mouseX?: number | null,
	mouseY?: number | null,
): StagePosAndScale => {
	const oldScale = stagePos.scale;
	const centreX = wrapperRect.left + wrapperRect.width / 2;
	const centreY = wrapperRect.top + wrapperRect.height / 2;
	const mouseRelativeX = (typeof mouseX === 'number' ? mouseX : centreX) - wrapperRect.left;
	const mouseRelativeY = (typeof mouseY === 'number' ? mouseY : centreY) - wrapperRect.top;

	const mousePointTo = {
		x: mouseRelativeX / oldScale - stagePos.x / oldScale,
		y: mouseRelativeY / oldScale - stagePos.y / oldScale,
	};

	return {
		x: -(mousePointTo.x - mouseRelativeX / nextScale) * nextScale,
		y: -(mousePointTo.y - mouseRelativeY / nextScale) * nextScale,
		scale: nextScale,
	};
};
